/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { EventTrackerActions, EventTrackerCategories, ExecType } from '../../enums/event-tracker.enum';
import { CategoryType } from '../consent/category-type.enum';
import { ConsentBasedTrackerService } from '../consent/consent-based-tracker.service';
import { CampaignParamsService } from './campaign-params.service';
import { MatomoLoaderService } from './matomo-loader.service';

@Injectable({ providedIn: 'root' })
export class EventTrackerService extends ConsentBasedTrackerService {
  private readonly campaignParamsService = inject(CampaignParamsService);
  private readonly matomoLoader = inject(MatomoLoaderService);
  private readonly router = inject(Router);
  private routerSubscription: Subscription | null = null;
  private lastPageUrl = '';

  constructor() {
    super(CategoryType.ANALYTICS);
    this.campaignParamsService.captureFromUrl(location.href);
  }

  private get execArray(): any[] {
    return (globalThis as any)._paq || [];
  }

  protected enable(): void {
    this.matomoLoader.init().then(() => {
      this.execArray.push(['rememberConsentGiven']);
      this.enabled = true;
      this.collectEvents = false;
      this.queue.flush();
      this.startPageTracking();
    });
  }

  protected disable(): void {
    this.stopPageTracking();
    this.execArray.push(['forgetConsentGiven']);
    this.enabled = false;
    this.collectEvents = false;
    this.queue.clear();
  }

  private startPageTracking(): void {
    this.trackPageView();
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.trackPageView());
  }

  private stopPageTracking(): void {
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
  }

  private trackPageView(): void {
    const currentUrl = this.campaignParamsService.getTrackingUrl(location.href);
    if (this.lastPageUrl) {
      this.execArray.push(['setReferrerUrl', this.lastPageUrl]);
    }
    this.execArray.push(['setCustomUrl', currentUrl]);
    this.execArray.push(['setDocumentTitle', document.title]);
    this.execArray.push(['trackPageView']);
    this.lastPageUrl = currentUrl;
  }

  trackLogin(customerName: string): void {
    this.trackEvent(EventTrackerCategories.LOGIN, customerName);
  }

  trackCustomerSettingsAutomatedTransfersTabOpen(): void {
    this.trackEvent(
      EventTrackerCategories.CUSTOMER_SETTINGS,
      EventTrackerActions.CUSTOMER_SETTINGS_AUTOMATED_TRANSFERS_TAB_OPEN,
    );
  }

  trackCustomerSettingsAutomatedTransfersConfigSave(): void {
    this.trackEvent(
      EventTrackerCategories.CUSTOMER_SETTINGS,
      EventTrackerActions.CUSTOMER_SETTINGS_AUTOMATED_TRANSFERS_CONFIG_SAVE,
    );
  }

  trackCustomerSettingsAutomatedTransfersNotificationsTabOpen(): void {
    this.trackEvent(
      EventTrackerCategories.CUSTOMER_SETTINGS,
      EventTrackerActions.CUSTOMER_SETTINGS_AUTOMATED_TRANSFERS_NOTIFICATIONS_TAB_OPEN,
    );
  }

  trackCustomerSettingsAutomatedTransfersNotificationTypeToggle(enabled: boolean): void {
    this.trackEvent(
      EventTrackerCategories.CUSTOMER_SETTINGS,
      enabled
        ? EventTrackerActions.CUSTOMER_SETTINGS_AUTOMATED_TRANSFERS_NOTIFICATION_TYPE_ENABLE
        : EventTrackerActions.CUSTOMER_SETTINGS_AUTOMATED_TRANSFERS_NOTIFICATION_TYPE_DISABLE,
    );
  }

  trackCustomerSettingsAutomatedTransfersNotificationsSave(): void {
    this.trackEvent(
      EventTrackerCategories.CUSTOMER_SETTINGS,
      EventTrackerActions.CUSTOMER_SETTINGS_AUTOMATED_TRANSFERS_NOTIFICATIONS_SAVE,
    );
  }

  trackAutomatedTransfersNotificationAcknowledge(): void {
    this.trackEvent(
      EventTrackerCategories.AUTOMATED_TRANSFERS_NOTIFICATION_CENTER,
      EventTrackerActions.AUTOMATED_TRANSFERS_NOTIFICATION_ACKNOWLEDGE,
    );
  }

  trackAutomatedTransfersNotificationAcknowledgeAll(): void {
    this.trackEvent(
      EventTrackerCategories.AUTOMATED_TRANSFERS_NOTIFICATION_CENTER,
      EventTrackerActions.AUTOMATED_TRANSFERS_NOTIFICATION_ACKNOWLEDGE_ALL,
    );
  }

  trackAutomatedTransfersNotificationCenterOpen(): void {
    this.trackEvent(
      EventTrackerCategories.AUTOMATED_TRANSFERS_NOTIFICATION_CENTER,
      EventTrackerActions.AUTOMATED_TRANSFERS_NOTIFICATION_CENTER_OPEN,
    );
  }

  trackAutomatedTransfersItemsOnTheRoadNavigationOpen(): void {
    this.trackEvent(
      EventTrackerCategories.AUTOMATED_TRANSFERS_NAVIGATION,
      EventTrackerActions.AUTOMATED_TRANSFERS_ITEMS_ON_THE_ROAD_NAVIGATION_OPEN,
    );
  }

  trackAutomatedTransfersScheduledTransfersNavigationOpen(): void {
    this.trackEvent(
      EventTrackerCategories.AUTOMATED_TRANSFERS_NAVIGATION,
      EventTrackerActions.AUTOMATED_TRANSFERS_SCHEDULED_TRANSFERS_NAVIGATION_OPEN,
    );
  }

  trackItemsOnTheRoadPause(): void {
    this.trackEvent(EventTrackerCategories.ITEMS_ON_THE_ROAD, EventTrackerActions.ITEMS_ON_THE_ROAD_PAUSE);
  }

  trackItemsOnTheRoadResume(): void {
    this.trackEvent(EventTrackerCategories.ITEMS_ON_THE_ROAD, EventTrackerActions.ITEMS_ON_THE_ROAD_RESUME);
  }

  trackItemsOnTheRoadCancel(): void {
    this.trackEvent(EventTrackerCategories.ITEMS_ON_THE_ROAD, EventTrackerActions.ITEMS_ON_THE_ROAD_CANCEL);
  }

  trackItemsOnTheRoadForceTransfer(): void {
    this.trackEvent(EventTrackerCategories.ITEMS_ON_THE_ROAD, EventTrackerActions.ITEMS_ON_THE_ROAD_FORCE_TRANSFER);
  }

  trackScheduledTransferCancel(): void {
    this.trackEvent(EventTrackerCategories.SCHEDULED_TRANSFERS, EventTrackerActions.SCHEDULED_TRANSFERS_CANCEL);
  }

  trackScheduledTransferDeactivate(): void {
    this.trackEvent(EventTrackerCategories.SCHEDULED_TRANSFERS, EventTrackerActions.SCHEDULED_TRANSFERS_DEACTIVATE);
  }

  trackScheduledTransferActivate(): void {
    this.trackEvent(EventTrackerCategories.SCHEDULED_TRANSFERS, EventTrackerActions.SCHEDULED_TRANSFERS_ACTIVATE);
  }

  private trackEvent(category: EventTrackerCategories, action: string): void {
    this.handleEvent<[ExecType, EventTrackerCategories, string]>(
      [ExecType.TRACK_EVENT, category, action],
      (eventPayload) => this.originalTrackEvent(eventPayload),
    );
  }

  private originalTrackEvent([method, category, action]: [ExecType, EventTrackerCategories, string]): void {
    this.execArray.push([method, category, action]);
  }
}
